import type { QuerySpec } from '@helios/powersync-client-core';

/** Row shape from the vital_definitions PowerSync table. */
export interface VitalDefinitionRow {
  id: string;
  code: string;
  name: string;
  unit: string | null;
  other_code: string | null;
  value_type: string;
  active: number; // 0 | 1 (SQLite boolean)
  mandatory: number; // 0 | 1
  sort_order: number;
  category: string | null;
  deleted_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/**
 * All non-deleted vital definitions, ordered by sort_order then code.
 */
export function qVitalDefinitions(): QuerySpec<[]> {
  return {
    key: ['vital-definitions', 'all'] as const,
    sql: `SELECT * FROM vital_definitions WHERE deleted_at IS NULL ORDER BY sort_order ASC, code ASC`,
    params: [],
    watch: ['vital_definitions'],
  };
}

/**
 * Count of non-deleted vital definitions.
 */
export function qVitalDefinitionsCount(): QuerySpec<[]> {
  return {
    key: ['vital-definitions', 'count'] as const,
    sql: `SELECT COUNT(*) as cnt FROM vital_definitions WHERE deleted_at IS NULL`,
    params: [],
    watch: ['vital_definitions'],
  };
}

// ─── Patient Vital Entries ───

/** Row shape from patient_vital_entries PowerSync table. */
export interface PatientVitalEntryRow {
  id: string;
  patient_id: string;
  recorded_at: string;
  vital_definition_id: string;
  value_number: number | null;
  value_text: string | null;
  value_date: string | null;
  value_boolean: number | null; // 0 | 1
  notes: string | null;
  queue_entry_id: string | null;
  appointment_id: string | null;
  encounter_id: string | null;
  source: string | null; // 'queue' | 'patient' | 'encounter'
  deleted_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** Row from patient_vital_entries JOINed with vital_definitions. */
export interface PatientVitalEntryWithDefinitionRow extends PatientVitalEntryRow {
  code: string;
  name: string;
  unit: string | null;
  value_type: string;
  category: string | null;
  sort_order: number;
  mandatory: number; // 0 | 1
}

/**
 * Active, non-deleted vital definitions for the entry form.
 */
export function qActiveVitalDefinitions(): QuerySpec<[]> {
  return {
    key: ['vital-definitions', 'active'] as const,
    sql: `SELECT * FROM vital_definitions WHERE deleted_at IS NULL AND active = 1 ORDER BY sort_order ASC, code ASC`,
    params: [],
    watch: ['vital_definitions'],
  };
}

/**
 * Latest recorded_at timestamp for a patient's vitals.
 */
export function qLatestPatientVitalsRecordedAt(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient-vital-entries', 'latest-recorded-at', patientId] as const,
    sql: `SELECT recorded_at FROM patient_vital_entries WHERE patient_id = ? AND deleted_at IS NULL ORDER BY recorded_at DESC LIMIT 1`,
    params: [patientId],
    watch: ['patient_vital_entries'],
  };
}

/**
 * Patient vital entries for a specific recorded_at session, joined with definitions.
 */
export function qPatientVitalsByRecordedAt(
  patientId: string,
  recordedAt: string,
): QuerySpec<[string, string]> {
  return {
    key: ['patient-vital-entries', 'by-recorded-at', patientId, recordedAt] as const,
    sql: `
      SELECT
        e.id, e.patient_id, e.recorded_at, e.vital_definition_id,
        e.value_number, e.value_text, e.value_date, e.value_boolean,
        e.notes, e.deleted_at, e.updated_at, e.updated_by,
        d.code, d.name, d.unit, d.value_type, d.category, d.sort_order, d.mandatory
      FROM patient_vital_entries e
      JOIN vital_definitions d ON d.id = e.vital_definition_id
      WHERE e.patient_id = ? AND e.recorded_at = ? AND e.deleted_at IS NULL
      ORDER BY d.sort_order ASC, d.code ASC
    `,
    params: [patientId, recordedAt],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

/**
 * Latest recorded value for each vital definition for a patient.
 * Uses a correlated subquery to pick the most recent entry per definition,
 * so vitals from different sessions are all represented.
 */
export function qLatestPatientVitals(patientId: string): QuerySpec<[string, string]> {
  return {
    key: ['patient-vital-entries', 'latest', patientId] as const,
    sql: `
      SELECT
        e.id, e.patient_id, e.recorded_at, e.vital_definition_id,
        e.value_number, e.value_text, e.value_date, e.value_boolean,
        e.notes, e.deleted_at, e.updated_at, e.updated_by,
        d.code, d.name, d.unit, d.value_type, d.category, d.sort_order, d.mandatory
      FROM patient_vital_entries e
      JOIN vital_definitions d ON d.id = e.vital_definition_id
      WHERE e.patient_id = ? AND e.deleted_at IS NULL
        AND e.recorded_at = (
          SELECT MAX(e2.recorded_at)
          FROM patient_vital_entries e2
          WHERE e2.patient_id = ? AND e2.vital_definition_id = e.vital_definition_id AND e2.deleted_at IS NULL
        )
        AND d.active = 1 AND d.deleted_at IS NULL
      ORDER BY d.sort_order ASC, d.code ASC
    `,
    params: [patientId, patientId],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

/**
 * Recent vitals history — last 10 recording sessions for a patient,
 * joined with definitions. Results ordered by recorded_at DESC then sort_order.
 */
export function qPatientVitalsHistory(patientId: string): QuerySpec<[string, string]> {
  return {
    key: ['patient-vital-entries', 'history', patientId] as const,
    sql: `
      SELECT
        e.id, e.patient_id, e.recorded_at, e.vital_definition_id,
        e.value_number, e.value_text, e.value_date, e.value_boolean,
        e.notes, e.deleted_at, e.updated_at, e.updated_by,
        d.code, d.name, d.unit, d.value_type, d.category, d.sort_order, d.mandatory
      FROM patient_vital_entries e
      JOIN vital_definitions d ON d.id = e.vital_definition_id
      WHERE e.patient_id = ? AND e.deleted_at IS NULL AND d.deleted_at IS NULL
        AND e.recorded_at IN (
          SELECT DISTINCT recorded_at
          FROM patient_vital_entries
          WHERE patient_id = ? AND deleted_at IS NULL
          ORDER BY recorded_at DESC
          LIMIT 10
        )
      ORDER BY e.recorded_at DESC, d.sort_order ASC, d.code ASC
    `,
    params: [patientId, patientId],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

/**
 * Full vitals history — every recording session for a patient, joined with
 * definitions. Same shape as qPatientVitalsHistory but without the 10-session
 * cap. Used by the "All Vitals" drawer.
 */
export function qAllPatientVitalsHistory(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient-vital-entries', 'all-history', patientId] as const,
    sql: `
      SELECT
        e.id, e.patient_id, e.recorded_at, e.vital_definition_id,
        e.value_number, e.value_text, e.value_date, e.value_boolean,
        e.notes, e.deleted_at, e.updated_at, e.updated_by,
        d.code, d.name, d.unit, d.value_type, d.category, d.sort_order, d.mandatory
      FROM patient_vital_entries e
      JOIN vital_definitions d ON d.id = e.vital_definition_id
      WHERE e.patient_id = ? AND e.deleted_at IS NULL AND d.deleted_at IS NULL
      ORDER BY e.recorded_at DESC, d.sort_order ASC, d.code ASC
    `,
    params: [patientId],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

/**
 * Latest vitals recorded today for a patient, joined with definitions.
 * Uses the most recent recorded_at session from today only.
 */
export function qLatestPatientVitalsToday(
  patientId: string,
  todayDate: string,
): QuerySpec<[string, string, string, string]> {
  return {
    key: ['patient-vital-entries', 'latest-today', patientId, todayDate] as const,
    sql: `
      SELECT
        e.id, e.patient_id, e.recorded_at, e.vital_definition_id,
        e.value_number, e.value_text, e.value_date, e.value_boolean,
        e.notes, e.deleted_at, e.updated_at, e.updated_by,
        d.code, d.name, d.unit, d.value_type, d.category, d.sort_order, d.mandatory
      FROM patient_vital_entries e
      JOIN vital_definitions d ON d.id = e.vital_definition_id
      WHERE e.patient_id = ? AND e.deleted_at IS NULL
        AND e.recorded_at >= (? || 'T00:00:00')
        AND e.recorded_at = (
          SELECT MAX(e2.recorded_at)
          FROM patient_vital_entries e2
          WHERE e2.patient_id = ? AND e2.deleted_at IS NULL
            AND e2.recorded_at >= (? || 'T00:00:00')
        )
        AND d.active = 1 AND d.deleted_at IS NULL
      ORDER BY d.sort_order ASC, d.code ASC
    `,
    params: [patientId, todayDate, patientId, todayDate],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

/**
 * Vitals recorded for a specific queue entry, joined with definitions.
 */
export function qVitalsByQueueEntryId(queueEntryId: string): QuerySpec<[string]> {
  return {
    key: ['patient-vital-entries', 'by-queue-entry', queueEntryId] as const,
    sql: `
      SELECT
        e.id, e.patient_id, e.recorded_at, e.vital_definition_id,
        e.value_number, e.value_text, e.value_date, e.value_boolean,
        e.notes, e.deleted_at, e.updated_at, e.updated_by,
        d.code, d.name, d.unit, d.value_type, d.category, d.sort_order, d.mandatory
      FROM patient_vital_entries e
      JOIN vital_definitions d ON d.id = e.vital_definition_id
      WHERE e.queue_entry_id = ? AND e.deleted_at IS NULL
        AND d.active = 1 AND d.deleted_at IS NULL
      ORDER BY d.sort_order ASC, d.code ASC
    `,
    params: [queueEntryId],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}

/**
 * Vitals recorded for a specific encounter, joined with definitions.
 */
export function qVitalsByEncounterId(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient-vital-entries', 'by-encounter', encounterId] as const,
    sql: `
      SELECT
        e.id, e.patient_id, e.recorded_at, e.vital_definition_id,
        e.value_number, e.value_text, e.value_date, e.value_boolean,
        e.notes, e.deleted_at, e.updated_at, e.updated_by,
        d.code, d.name, d.unit, d.value_type, d.category, d.sort_order, d.mandatory
      FROM patient_vital_entries e
      JOIN vital_definitions d ON d.id = e.vital_definition_id
      WHERE e.encounter_id = ? AND e.deleted_at IS NULL
        AND d.active = 1 AND d.deleted_at IS NULL
      ORDER BY e.recorded_at DESC, d.sort_order ASC, d.code ASC
    `,
    params: [encounterId],
    watch: ['patient_vital_entries', 'vital_definitions'],
  };
}
